import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, FileText, Loader2, Clock } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import EmptyState from '../EmptyState';
import StateBlock from '../ui/StateBlock';
import Badge from '../ui/Badge';
import { formatDate, formatHoursClock } from '../../utils/format';
import { israelToday } from '../../utils/israelTime';
import { fileNameFromDisposition } from '../../utils/logDetailModel';
import { GROUP_BYS, groupLogs, isMonthApproved, type GroupBy, type GroupableLog } from '../../utils/workLogGroups';
import type { WorkLog } from '../../types';

/**
 * D-319 sec.9 - the ONE shared piece behind the work-logs page and the hours
 * tabs (customer / project / engagement): "קבץ לפי" + a group per value with
 * its total (opens to its logs), the month-approval column, and the PDF of
 * what is on screen. No new page.
 */

/** The server filters of the PDF / CSV export (what the screen is showing). */
export interface LogsExportFilters {
  start_date?: string;
  end_date?: string;
  organization_id?: string;
  customer_id?: string;
  engagement_id?: string;
  agent_id?: string;
  project_id?: string;
  /** D-319 sec.9: the tab of "פירוט לוגים" the export follows. */
  tab?: string;
  /** "פירוט לוגים" PDF: also print the logs that are not billable (off by default). */
  include_non_billable?: boolean;
}

export function GroupByControl({ value, onChange, id = 'wl-group-by' }: { value: GroupBy; onChange: (v: GroupBy) => void; id?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-sm text-text-2 whitespace-nowrap">{t('logsView.groupBy')}</label>
      <select id={id} className="input py-1.5 w-auto" value={value} onChange={(e) => onChange(e.target.value as GroupBy)}>
        {GROUP_BYS.map((g) => <option key={g} value={g}>{t(`logsView.group.${g}`)}</option>)}
      </select>
    </div>
  );
}

/** "העובד אישר" / "עוד לא אישר" - the month approval of the log's employee. */
export function MonthApprovalBadge({ log }: { log: { month_approved?: boolean | null } }) {
  const { t } = useTranslation();
  return isMonthApproved(log)
    ? <Badge tone="success" dot>{t('logsView.approved')}</Badge>
    : <Badge tone="neutral">{t('logsView.notApproved')}</Badge>;
}

/** Downloads the PDF (or CSV) of the current filters + grouping (the server draws it with the same scope as the CSV). */
export function WorkLogsPdfButton({ filters, groupBy, format = 'pdf' }: { filters: LogsExportFilters; groupBy: GroupBy; format?: 'pdf' | 'csv' }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const url = api.getExportWorkLogsUrl({ ...filters, format, group_by: groupBy });
      const response = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        toast('error', apiErrorMessage(body, t('logsView.pdfError')));
        return;
      }
      const blobUrl = URL.createObjectURL(await response.blob());
      const a = document.createElement('a');
      a.href = blobUrl;
      // FIX-LOGDETAIL-PDF: "פירוט לוגים" names its file after the customer + month (the server's header); the rest keep the old name.
      a.download = fileNameFromDisposition(response.headers.get('Content-Disposition')) ?? `work-logs-${israelToday()}.${format}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', t('logsView.pdfError'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="btn btn-secondary" onClick={() => void run()} disabled={busy}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <FileText className="w-4 h-4" aria-hidden="true" />}
      <span>{format === 'csv' ? t('logsView.csv') : t('logsView.pdf')}</span>
    </button>
  );
}

type Row = WorkLog & GroupableLog;

/** The groups of `logs`, each with its total; a group opens to its logs. */
export default function GroupedWorkLogs({ logs, groupBy, showWorker = true, showApproval = true, initiallyOpen = false, selection }: {
  logs: Row[]; groupBy: GroupBy; showWorker?: boolean; showApproval?: boolean; initiallyOpen?: boolean;
  /** D-348: optional row checkboxes (the "העברת לוגים" selection). Absent = the table as before. */
  selection?: { selected: ReadonlySet<string>; onToggle: (id: string) => void };
}) {
  const { t } = useTranslation();
  const groups = groupLogs(logs, groupBy);
  const [open, setOpen] = useState<Set<string>>(() => new Set(initiallyOpen ? groups.map((g) => (g.isEmpty ? '__none' : g.key)) : []));
  const th = 'px-3 py-2 text-start text-xs font-medium text-surface-600';
  const td = 'px-3 py-2 text-sm text-surface-900';
  const toggle = (k: string) => setOpen((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  if (logs.length === 0) return <EmptyState icon={Clock} title={t('logsView.empty')} />;

  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const gk = g.isEmpty ? '__none' : g.key;
        // "none" = one flat table that is always open.
        const isOpen = groupBy === 'none' || open.has(gk);
        return (
          <section key={gk} className="rounded-lg border border-surface-200 bg-surface">
            {groupBy !== 'none' && (
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-start"
                aria-expanded={isOpen}
                onClick={() => toggle(gk)}
              >
                <span className="flex items-center gap-2 font-medium text-surface-900">
                  {isOpen ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronLeft className="w-4 h-4" aria-hidden="true" />}
                  <bdi>{g.isEmpty ? t(`logsView.none.${groupBy}`) : (groupBy === 'day' ? formatDate(g.label) : g.label)}</bdi>
                  <span className="text-xs font-normal text-text-2">{t('logsView.count', { count: g.logs.length })}</span>
                </span>
                <span className="tabular-nums text-sm font-semibold" aria-label={t('logsView.total')}>{formatHoursClock(g.minutes)}</span>
              </button>
            )}
            {isOpen && (
              <div className="overflow-x-auto border-t border-surface-100">
                <table className="w-full">
                  <caption className="sr-only">{g.isEmpty ? t(`logsView.none.${groupBy}`) : g.label}</caption>
                  <thead className="border-b border-surface-200">
                    <tr>
                      {selection && <th scope="col" className={th}><span className="sr-only">{t('moveLogs.select')}</span></th>}
                      <th scope="col" className={th}>{t('logsView.colDate')}</th>
                      {showWorker && <th scope="col" className={th}>{t('logsView.colWorker')}</th>}
                      <th scope="col" className={th}>{t('logsView.colTask')}</th>
                      <th scope="col" className={th}>{t('logsView.colSummary')}</th>
                      <th scope="col" className={`${th} text-end`}>{t('logsView.colHours')}</th>
                      {showApproval && <th scope="col" className={th}>{t('logsView.colApproval')}</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100">
                    {g.logs.map((l) => (
                      <tr key={l.id}>
                        {selection && (
                          <td className={td}>
                            <input type="checkbox" checked={selection.selected.has(l.id)} onChange={() => selection.onToggle(l.id)}
                              aria-label={t('moveLogs.selectLog', { date: formatDate(l.start_time) })} data-testid="log-select" />
                          </td>
                        )}
                        <td className={`${td} whitespace-nowrap tabular-nums`}>{formatDate(l.start_time)}</td>
                        {showWorker && <td className={td}>{l.agent_name ?? '—'}</td>}
                        <td className={td}><bdi>{l.ticket_title ?? '—'}</bdi></td>
                        <td className={`${td} max-w-64 truncate text-text-2`} title={l.summary}>{l.summary || '—'}</td>
                        <td className={`${td} text-end tabular-nums`}>{formatHoursClock(l.duration_minutes ?? 0)}</td>
                        {showApproval && <td className={td}><MonthApprovalBadge log={l} /></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

/**
 * The self-contained version for the hours tabs (customer / project): fetches
 * the logs of its scope for a month window, with the grouping and the PDF.
 */
export function WorkLogsExplorer({ scope, from, to, defaultGroupBy = 'worker', queryKey }: {
  scope: { organization_id?: string; project_id?: string; engagement_id?: string };
  from?: string;
  to?: string;
  defaultGroupBy?: GroupBy;
  queryKey: string;
}) {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState<GroupBy>(defaultGroupBy);
  const { data, isLoading, isError, refetch, isFetching, error } = useQuery({
    queryKey: ['logs-explorer', queryKey, scope, from, to],
    queryFn: () => api.getWorkLogs({ ...scope, ...(from ? { start_date: from } : {}), ...(to ? { end_date: to } : {}), limit: 1000 }),
  });
  const rows = (data?.data ?? []) as Row[];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <GroupByControl value={groupBy} onChange={setGroupBy} id={`wl-group-by-${queryKey}`} />
        <WorkLogsPdfButton filters={{ ...scope, ...(from ? { start_date: from } : {}), ...(to ? { end_date: to } : {}) }} groupBy={groupBy} />
      </div>
      {isLoading ? <StateBlock kind="loading" />
        : isError ? <StateBlock kind="error" error={error} title={t('logsView.loadError')} onRetry={() => refetch()} retrying={isFetching} />
          : <GroupedWorkLogs logs={rows} groupBy={groupBy} />}
    </div>
  );
}
